import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';

import { EquipmentBase } from '../../models/equipment-base';
import { HighlightOptionsValue } from '../../../core/services/highlight-products.service';
import { ProductGroup } from '../../models/product-group';
import { UnitConverter } from '../../../core/utils/unitConverter';
import { MeasurementUnitTranslatePipe } from '../../../core/pipes/measurement-unit-translate.pipe';
import { PhIconWrapperComponent } from '../../../core/components/ph-icon-wrapper/ph-icon-wrapper.component';
import { SHARED_MODULES } from '../../../shared.';

import { Utils } from '../../../core/services/utils.service';

type HighlightLegendOptions = {
	id: number;
	title: string;
	color: string;
	tooltipOptions: {
		percent: number;
		units: number;
		unit: string;
	};
}

type HighlightData = {
	id: number;
	category?: string;
	brand?: string;
	supplier?: string;
	assortment_type?: string;
	status?: string;
	color: string;
	format?: string;
}

const HIGHLIGHT_MAPPER = {
	[HighlightOptionsValue.Categories]: 'category',
	[HighlightOptionsValue.Brands]: 'brand',
	[HighlightOptionsValue.Suppliers]: 'supplier',
	[HighlightOptionsValue.InactiveProducts]: 'assortment_type',
	[HighlightOptionsValue.ActiveProducts]: 'assortment_type',
	[HighlightOptionsValue.Status]: 'status',
	[HighlightOptionsValue.Format]: 'format'
};

@Component({
	selector: 'app-highlight-legend[planogram]',
	templateUrl: './highlight-legend.component.html',
	styleUrls: ['./highlight-legend.component.scss'],
	imports: [...SHARED_MODULES, PhIconWrapperComponent]
})
export class HighlightLegendComponent implements OnInit {

	public highlightLegendOpened: boolean = true;
	public highlightLegendOptions: Array<HighlightLegendOptions> = [];
	public highlightLegendProducts: { [key: number]: HighlightData } = {};
	public highlightTooltipTitle: string = 'HIGHLIGHT_PRODUCTS_ACTIVE_LEGEND';

	@Input('highlight') highlightValue: HighlightOptionsValue = HighlightOptionsValue.Empty;
	@Input('planogram') planogram: EquipmentBase;

	@Input('products') set options(value: { [key: number]: HighlightData }) {
		const linearMeters: number = this.planogram?.LinearMeters;

		this.highlightLegendOptions.length = 0;
		this.highlightLegendProducts = value;

		Object.values(value)
			.filter((item: HighlightData, idx: number, array: Array<HighlightData>) => {
				return array.findIndex((i: HighlightData) => i.id == item.id) === idx;
			})
			.forEach((item: HighlightData) => {
				const units: number = this.getOptionUnits(item.id);
				if (units > 0) {
					this.highlightLegendOptions.push({
						id: item.id,
						title: item[HIGHLIGHT_MAPPER[this.highlightValue]] || this.getEmptyTranslate(),
						color: item.color,
						tooltipOptions: {
							percent: (linearMeters ? Utils.round(units / linearMeters * 100, 2) : 0),
							units: UnitConverter.getConvertValue(units, 'distance', 'inches', 3),
							unit: this.measurementUnitTranslatePipe.transform('distance')
						}
					});
				}
			});

		this.highlightLegendOptions.sort((a: HighlightLegendOptions, b: HighlightLegendOptions) => {
			return b.tooltipOptions.percent - a.tooltipOptions.percent;
		});
	};

	@Input('update') updateSubject: Subject<void>;

	@Output('opened') opened: EventEmitter<boolean> = new EventEmitter<boolean>();

	constructor(
		private translateService: TranslateService,
		private measurementUnitTranslatePipe: MeasurementUnitTranslatePipe
	) {}

	ngOnInit(): void {
		if (this.updateSubject) {
			this.updateSubject.subscribe(() => {
				this.options = this.highlightLegendProducts;
			});
		}
	}

	private getOptionUnits(id: number | boolean | string): number {
		let units: number = 0;
		this.planogram.products
			.filter((pg: ProductGroup) => {
				switch (this.highlightValue) {
					case HighlightOptionsValue.Format: return pg.product.is_master_position === id;
					case HighlightOptionsValue.Brands: return pg.product.brandsUniqueId === id;
					case HighlightOptionsValue.Suppliers: return pg.product.suppliersUniqueId === id;
					case HighlightOptionsValue.Categories: return pg.product.categoriesUniqueId === id;
					case HighlightOptionsValue.Status: return pg.product.statusesUniqueId === id;
					default: return pg.product[HIGHLIGHT_MAPPER[this.highlightValue]].id == id;
				}
			})
			.filter((pg: ProductGroup) => pg.availability)
			.forEach((pg: ProductGroup) => units += pg.width);
		return units;
	}

	private getEmptyTranslate(): string {
		let translate: string = '';

		if (this.highlightValue == HighlightOptionsValue.Brands) {
			this.translateService.get('NO_BRAND').subscribe((t: string) => translate = t);
		}

		if (this.highlightValue == HighlightOptionsValue.Categories) {
			this.translateService.get('WITHOUT_CATEGORY').subscribe((t: string) => translate = t);
		}

		if (this.highlightValue == HighlightOptionsValue.Suppliers) {
			this.translateService.get('WITHOUT_SUPPLIER').subscribe((t: string) => translate = t);
		}

		if (this.highlightValue == HighlightOptionsValue.ActiveProducts) {
			this.translateService.get('COMMON.ASSORTMENT.ACTIVE_ONE').subscribe((t: string) => translate = t);
		}

		if (this.highlightValue == HighlightOptionsValue.InactiveProducts) {
			this.translateService.get('COMMON.ASSORTMENT.INACTIVE_ONE').subscribe((t: string) => translate = t);
		}

		return translate;
	}

	setOpened(): void {
		this.highlightLegendOpened = !this.highlightLegendOpened;
		this.highlightTooltipTitle = this.highlightLegendOpened
			? 'HIGHLIGHT_PRODUCTS_ACTIVE_LEGEND'
			: 'HIGHLIGHT_PRODUCTS_INACTIVE_LEGEND';
		setTimeout(() => this.opened.emit(this.highlightLegendOpened), 600);
	}
}